/**
 * Clan-shape helpers — centralise the legacy-vs-sqrt curve distinction so
 * every card, sheet, and dashboard renders honest metrics for both cohorts.
 *
 * A clan is treated as `sqrt` (unlimited supply) if EITHER:
 *   • curve_type === 'sqrt'   (explicit — set on all clans minted post-2026-02), or
 *   • !supply_cap             (missing / null / 0 — defensive for pre-migration data)
 *
 * Legacy `linear` clans still have `curve_type === 'linear'` AND a finite
 * `supply_cap`, and continue to render the old %-minted / Bits-left UI.
 */
export function isSqrtClan(clan) {
  if (!clan) return false;
  if (clan.curve_type === 'sqrt') return true;
  if (clan.curve_type === 'linear') return false;
  // Fallback: legacy docs without `curve_type` — treat missing supply_cap as sqrt
  return !clan.supply_cap;
}

/**
 * Human-friendly "launched X ago" — "just now" / "5m ago" / "3h ago" /
 * "3d ago" / "2w ago". Returns null if the launch timestamp is missing OR
 * still in the future (pre-launch clans handle their own countdown).
 */
export function fmtLaunchedAgo(launchAt) {
  if (!launchAt) return null;
  const t = new Date(launchAt).getTime();
  if (!Number.isFinite(t)) return null;
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 0) return null;                              // still pre-launch
  if (s < 60) return 'just launched';
  if (s < 3600) return `Launched ${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `Launched ${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `Launched ${Math.floor(s / 86400)}d ago`;
  return `Launched ${Math.floor(s / 604800)}w ago`;
}

/**
 * Human-friendly Bits count. 12 → "12" · 1_234 → "1.2K" · 1_234_567 → "1.2M".
 */
export function fmtBits(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}
